import React, { useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, Circle, useJsApiLoader } from '@react-google-maps/api';
import { CommanServices } from '@/services/commanServices';
import { GOOGLE_MAPS_LOADER_OPTIONS } from './map/MapGisTools';
import { useMapsUnavailable, MapUnavailable } from './map/MapUnavailable';
import { Globe2, Flame, MapPin, Layers3, Activity, TriangleAlert, Crosshair, RefreshCw, Gauge } from 'lucide-react';

const containerStyle = { width: '100%', height: '380px' };
const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };

const SEV_COLOR = { Critical: '#dc2626', High: '#ef4444', Medium: '#f97316', Low: '#eab308' };
const sevWeight = (s) => ({ Critical: 4, High: 3, Medium: 2, Low: 1 }[s] || 1);

const coordOf = (inc, locById) => {
  const g = inc?.gps_coordinates || {};
  let lat = g.latitude ?? g.lat ?? inc?.gps_latitude;
  let lng = g.longitude ?? g.lng ?? inc?.gps_longitude;
  if ((lat == null || lng == null) && Array.isArray(inc?.location)) {
    for (const id of inc.location) {
      const l = locById[String(id)];
      if (l && l.gps_latitude != null && l.gps_longitude != null) { lat = l.gps_latitude; lng = l.gps_longitude; break; }
    }
  }
  lat = Number(lat); lng = Number(lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const zoneName = (inc, locById) => {
  if (Array.isArray(inc?.location_names) && inc.location_names.length) return inc.location_names[0];
  if (Array.isArray(inc?.location)) {
    for (const id of inc.location) {
      const l = locById[String(id)];
      if (l) return l.location_name || l.name || 'Unknown zone';
    }
  }
  return 'Unzoned';
};

export default function DashboardGeoAnalyticsWidget() {
  const [incidents, setIncidents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heatmapOn, setHeatmapOn] = useState(true);
  const [markersOn, setMarkersOn] = useState(true);
  const [dataMode, setDataMode] = useState('real'); // 'real' incidents or 'drill' exercises
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();

  const load = async () => {
    setLoading(true);
    try {
      const [incRes, locRes] = await Promise.all([
        CommanServices.getIncidentsData(),
        CommanServices.getlocationszonesdata(),
      ]);
      const inc = incRes?.status ? (await incRes.data) : [];
      const loc = locRes?.status ? (await locRes.data) : [];
      setIncidents(Array.isArray(inc) ? inc : (inc?.incidents || []));
      setLocations(Array.isArray(loc) ? loc : []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const locById = useMemo(() => {
    const m = {}; locations.forEach((l) => { if (l?.id) m[String(l.id)] = l; }); return m;
  }, [locations]);

  // Dataset follows the Real / Drill mode toggle
  const exData = useMemo(() => incidents.filter((i) => dataMode === 'drill'
    ? i?.is_exercise
    : (!i?.is_exercise && !i?.is_scenario)), [incidents, dataMode]);

  const points = useMemo(() => exData
    .map((inc) => ({ inc, pos: coordOf(inc, locById) }))
    .filter((p) => p.pos), [exData, locById]);

  const stats = useMemo(() => {
    const total = exData.length;
    const active = exData.filter((i) => ['active', 'open', 'in_progress'].includes(String(i.status || '').toLowerCase())).length;
    const critical = exData.filter((i) => ['Critical', 'High'].includes(i.severity)).length;
    const geocoded = points.length;
    const sev = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    exData.forEach((i) => { if (sev[i.severity] != null) sev[i.severity] += 1; });
    const zones = {};
    exData.forEach((i) => { const z = zoneName(i, locById); zones[z] = (zones[z] || 0) + 1; });
    const zoneList = Object.entries(zones).sort((a, b) => b[1] - a[1]);
    const hotspot = zoneList[0] || ['—', 0];
    const coverage = total ? Math.round((geocoded / total) * 100) : 0;
    return { total, active, critical, geocoded, sev, zoneList, hotspot, coverage, zoneCount: zoneList.length };
  }, [exData, points, locById]);

  const center = points.length ? points[0].pos : DEFAULT_CENTER;
  const maxZone = stats.zoneList[0]?.[1] || 1;

  const Kpi = ({ icon: Icon, label, value, sub, tone }) => (
    <div className="rounded-xl border border-gray-100 bg-white p-3" data-testid={`geo-kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
        <Icon className="h-3.5 w-3.5" style={{ color: tone }} /> {label}
      </div>
      <div className="text-2xl font-black text-gray-900 leading-tight mt-1">{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="geo-analytics-widget">
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-700 via-indigo-700 to-violet-700">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-white backdrop-blur">
            <Globe2 className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-base font-bold text-white leading-tight">Geospatial Analytics Atlas</h3>
            <p className="text-[12px] text-indigo-100">GIS-backed KPIs, spatial analysis &amp; {dataMode === 'drill' ? 'drill' : 'incident'} heatmap</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-white/15 p-0.5">
            <button data-testid="geo-mode-real" onClick={() => setDataMode('real')}
              className={`rounded-md px-3 py-1 text-xs font-bold transition-colors ${dataMode === 'real' ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/20'}`}>
              Real
            </button>
            <button data-testid="geo-mode-drill" onClick={() => setDataMode('drill')}
              className={`rounded-md px-3 py-1 text-xs font-bold transition-colors ${dataMode === 'drill' ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/20'}`}>
              Drill
            </button>
          </div>
          <button data-testid="geo-toggle-heatmap" onClick={() => setHeatmapOn((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${heatmapOn ? 'bg-white text-indigo-700' : 'bg-white/15 text-white hover:bg-white/25'}`}>
            <Flame className="h-3.5 w-3.5" /> Heatmap
          </button>
          <button data-testid="geo-toggle-markers" onClick={() => setMarkersOn((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${markersOn ? 'bg-white text-indigo-700' : 'bg-white/15 text-white hover:bg-white/25'}`}>
            <MapPin className="h-3.5 w-3.5" /> Markers
          </button>
          <button data-testid="geo-refresh" onClick={load} title="Refresh"
            className="flex items-center justify-center rounded-lg bg-white/15 text-white hover:bg-white/25 h-8 w-8">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-slate-50/60 border-b border-gray-100">
        <Kpi icon={Activity} label={dataMode === 'drill' ? 'Drills' : 'Incidents'} value={stats.total} sub="mapped dataset" tone="#6366f1" />
        <Kpi icon={Gauge} label="Active" value={stats.active} sub="live / open" tone="#10b981" />
        <Kpi icon={TriangleAlert} label="High Risk" value={stats.critical} sub={`${stats.total ? Math.round((stats.critical / stats.total) * 100) : 0}% of total`} tone="#ef4444" />
        <Kpi icon={Crosshair} label="On Map" value={`${stats.coverage}%`} sub={`${stats.geocoded} of ${stats.total} located`} tone="#0ea5e9" />
        <Kpi icon={Layers3} label="Sites" value={stats.zoneCount} sub="distinct locations" tone="#8b5cf6" />
        <Kpi icon={Flame} label="Hotspot" value={stats.hotspot[1]} sub={stats.hotspot[0]} tone="#f97316" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        {/* Map */}
        <div className="lg:col-span-2 relative border-r border-gray-100">
          {mapsUnavailable ? (
            <div style={containerStyle}><MapUnavailable center={center} label="Geospatial atlas" /></div>
          ) : !isLoaded ? (
            <div style={containerStyle} className="flex items-center justify-center bg-gray-100 text-gray-400 text-sm">Loading atlas…</div>
          ) : (
            <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={points.length ? 11 : 6}
              options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: true }}>
              {heatmapOn && points.map((p, i) => {
                const w = sevWeight(p.inc.severity);
                return (
                  <Circle key={`h-${i}`} center={p.pos} radius={500 + w * 350}
                    options={{ fillColor: SEV_COLOR[p.inc.severity] || '#ef4444', fillOpacity: Math.min(0.08 + w * 0.05, 0.32), strokeOpacity: 0, clickable: false, zIndex: 2 }} />
                );
              })}
              {markersOn && points.map((p, i) => (
                <Marker key={`m-${i}`} position={p.pos}
                  icon={window.google ? { path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: SEV_COLOR[p.inc.severity] || '#6366f1', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 1.5 } : undefined} />
              ))}
            </GoogleMap>
          )}
          {/* Severity legend */}
          <div className="absolute bottom-3 left-3 z-10 bg-white/95 backdrop-blur border border-gray-200 rounded-lg shadow-sm px-3 py-2 flex items-center gap-3">
            {Object.entries(SEV_COLOR).map(([k, c]) => (
              <span key={k} className="flex items-center gap-1 text-[10px] font-semibold text-gray-600">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />{k}
              </span>
            ))}
          </div>
        </div>

        {/* Spatial analysis panel */}
        <div className="p-4 space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">{dataMode === 'drill' ? 'Drill' : 'Incident'} density by zone</p>
            <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1" data-testid="geo-zone-list">
              {stats.zoneList.slice(0, 8).map(([z, n]) => (
                <div key={z}>
                  <div className="flex items-center justify-between text-[11px] mb-0.5">
                    <span className="text-gray-600 truncate pr-2">{z}</span>
                    <span className="font-bold text-gray-800">{n}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" style={{ width: `${(n / maxZone) * 100}%` }} />
                  </div>
                </div>
              ))}
              {stats.zoneList.length === 0 && <p className="text-xs text-gray-400">No zone data.</p>}
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Severity distribution</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(stats.sev).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-[11px]" style={{ backgroundColor: `${SEV_COLOR[k]}14` }}>
                  <span className="flex items-center gap-1 font-semibold" style={{ color: SEV_COLOR[k] }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: SEV_COLOR[k] }} />{k}
                  </span>
                  <span className="font-bold" style={{ color: SEV_COLOR[k] }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
